import { useState, useCallback } from "react";

/**
 * 이메일 발송 상태 타입
 */
export type EmailStatus = "pending" | "sending" | "success" | "failed";

/**
 * CSV 데이터 인터페이스
 * @description tmember 테이블 구조에 맞는 CSV 데이터 형식 + 발송 상태 관리
 */
export interface CsvData {
  email: string;
  firstName: string;
  lastName?: string;
  phone?: string;
  // 발송 상태 관리
  emailStatus?: EmailStatus;
  sentAt?: Date;
  errorMessage?: string;
}

/**
 * CSV 검증 결과 인터페이스
 */
export interface CsvValidationResult {
  isValid: boolean;
  errors: string[];
  lineNumber?: number;
}

/**
 * CSV 데이터 관리 커스텀 훅
 * @description CSV 데이터 파싱, 검증, 페이징 로직을 관리합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 2.0.0
 * @since 2025-01-15
 * @lastModified 2025-01-15
 */
export const useCsvData = (csvData: CsvData[]) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<EmailStatus | "all">("all");

  /**
   * 단일 행 CSV 데이터 검증
   */
  const validateCsvRow = useCallback(
    (row: any, lineNumber: number): CsvValidationResult => {
      const errors: string[] = [];

      // 필수 필드 검증
      if (
        !row.email ||
        typeof row.email !== "string" ||
        row.email.trim() === ""
      ) {
        errors.push(`Line ${lineNumber}: Email is required`);
      } else {
        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(row.email.trim())) {
          errors.push(`Line ${lineNumber}: Invalid email format`);
        }
      }

      if (
        !row.firstName ||
        typeof row.firstName !== "string" ||
        row.firstName.trim() === ""
      ) {
        errors.push(`Line ${lineNumber}: First name is required`);
      }

      // 선택 필드 길이 검증
      if (row.firstName && row.firstName.length > 50) {
        errors.push(
          `Line ${lineNumber}: First name too long (max 50 characters)`
        );
      }

      if (row.lastName && row.lastName.length > 50) {
        errors.push(
          `Line ${lineNumber}: Last name too long (max 50 characters)`
        );
      }

      if (row.phone && row.phone.length > 30) {
        errors.push(
          `Line ${lineNumber}: Phone number too long (max 30 characters)`
        );
      }

      return {
        isValid: errors.length === 0,
        errors,
        lineNumber: errors.length > 0 ? lineNumber : undefined,
      };
    },
    []
  );

  /**
   * 전체 CSV 데이터 검증
   */
  const validateAllCsvData = useCallback(
    (data: CsvData[]): CsvValidationResult => {
      const allErrors: string[] = [];

      if (!data || data.length === 0) {
        return {
          isValid: false,
          errors: ["No data to validate"],
        };
      }

      data.forEach((row, index) => {
        const lineNumber = index + 2; // 헤더 제외하고 1부터 시작하므로 +2
        const validation = validateCsvRow(row, lineNumber);
        if (!validation.isValid) {
          allErrors.push(...validation.errors);
        }
      });

      // 중복 이메일 검증
      const emails = data
        .map((row) => row.email?.trim().toLowerCase())
        .filter(Boolean);
      const duplicateEmails = emails.filter(
        (email, index) => emails.indexOf(email) !== index
      );

      if (duplicateEmails.length > 0) {
        allErrors.push(
          `Duplicate emails found: ${[...new Set(duplicateEmails)].join(", ")}`
        );
      }

      return {
        isValid: allErrors.length === 0,
        errors: allErrors,
      };
    },
    [validateCsvRow]
  );

  /**
   * CSV 데이터 정제
   */
  const cleanCsvData = useCallback((data: CsvData[]): CsvData[] => {
    return data
      .filter((row, index) => {
        // 빈 행 제거
        if (!row.email && !row.firstName) {
          console.log(`[CSV_CLEAN] Removing empty row at line ${index + 2}`);
          return false;
        }
        return true;
      })
      .map((row) => ({
        email: row.email?.trim() || "",
        firstName: row.firstName?.trim() || "",
        lastName: row.lastName?.trim() || undefined,
        phone: row.phone?.trim() || undefined,
      }));
  }, []);

  /**
   * 페이지 변경 핸들러
   */
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  /**
   * 페이지당 행 수 변경 핸들러
   */
  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  /**
   * 상태별 필터링된 데이터
   */
  const filteredData = csvData.filter((row) => {
    if (statusFilter === "all") return true;
    return (row.emailStatus || "pending") === statusFilter;
  });

  /**
   * 페이징된 데이터 계산 (필터링된 데이터 기준)
   */
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /**
   * 통계 정보 계산 (발송 상태 포함)
   */
  const stats = {
    totalCount: csvData.length,
    filteredCount: filteredData.length,
    validCount: csvData.filter((row) => row.email).length, // 이메일만 있으면 유효
    invalidCount: csvData.filter((row) => !row.email).length,
    currentPage: page + 1,
    totalPages: Math.ceil(filteredData.length / rowsPerPage),
    // 발송 상태별 통계
    pendingCount: csvData.filter(
      (row) => (row.emailStatus || "pending") === "pending"
    ).length,
    sendingCount: csvData.filter((row) => row.emailStatus === "sending").length,
    successCount: csvData.filter((row) => row.emailStatus === "success").length,
    failedCount: csvData.filter((row) => row.emailStatus === "failed").length,
  };

  /**
   * 상태 필터 변경 핸들러
   */
  const handleStatusFilterChange = useCallback(
    (status: EmailStatus | "all") => {
      setStatusFilter(status);
      setPage(0); // 첫 페이지로 리셋
    },
    []
  );

  /**
   * 실패한 이메일만 필터링
   */
  const getFailedEmails = useCallback(() => {
    return csvData.filter((row) => row.emailStatus === "failed");
  }, [csvData]);

  /**
   * 대기 중인 이메일만 필터링
   */
  const getPendingEmails = useCallback(() => {
    return csvData.filter(
      (row) => (row.emailStatus || "pending") === "pending"
    );
  }, [csvData]);

  return {
    // 상태
    page,
    rowsPerPage,
    paginatedData,
    filteredData,
    stats,
    statusFilter,

    // 핸들러
    handleChangePage,
    handleChangeRowsPerPage,
    handleStatusFilterChange,

    // 유틸리티 함수
    validateCsvRow,
    validateAllCsvData,
    cleanCsvData,
    getFailedEmails,
    getPendingEmails,
  };
};
